import { useMemo } from 'react'

import { useTranslation } from '~/app/i18n/client'

type useEarningHistoryProps = {
  transactions: Record<string, any>
  content: Record<string, any>
}

const useEarningHistory = ({
  transactions,
  content,
}: useEarningHistoryProps) => {
  const { t } = useTranslation()
  const defaultColumns = [
    {
      value: 'date',
      identifier: 'date',
      label: t('pawDashboards.earningHistory.date'),
      default: true,
    },
    {
      value: 'description',
      identifier: 'description',
      label: t('pawDashboards.earningHistory.description'),
      default: true,
    },
    {
      value: 'amount',
      identifier: 'amount',
      label: t('pawDashboards.earningHistory.amount'),
      default: true,
    },
  ]

  const { customColumns, metadata, currencies } = content

  const normalizedCurrencies = currencies?.map((c: string) => c?.toLowerCase())

  const columns = useMemo(() => {
    const metaColumns = metadata?.columns ?? []

    if (metaColumns?.length) {
      const allColumns = [...defaultColumns, ...customColumns]
      return metaColumns.map((columnName: string) => {
        const column = allColumns?.find(
          (col: any) =>
            col?.identifier?.toLowerCase() === columnName?.toLowerCase(),
        )

        if (column) {
          return column
        }
      })
    }

    return customColumns?.length
      ? [
          ...(defaultColumns ?? []).slice(0, 2),
          ...customColumns,
          ...(defaultColumns ?? []).slice(2),
        ]
      : defaultColumns
  }, [metadata?.columns])

  const getTotal = (transactions: { amount: number; currency: string }[]) => {
    return transactions?.reduce((acc, transaction) => {
      if (
        normalizedCurrencies?.includes(transaction?.currency?.toLowerCase())
      ) {
        acc = acc + (transaction?.amount ?? 0)
      }

      return acc
    }, 0)
  }

  const normalizedTransactions = useMemo(() => {
    return {
      ...transactions,
      interactionResponses: transactions?.interactionResponses
        ?.map((transaction: Record<string, any>) => ({
          ...transaction,
          followOnActions: transaction?.followOnActions?.filter(
            (action: { currency: string }) =>
              normalizedCurrencies?.includes(action?.currency?.toLowerCase()),
          ),
        }))
        ?.filter(
          (transaction: Record<string, any>) =>
            transaction?.followOnActions?.length,
        ),
    }
  }, [transactions])

  return {
    columns,
    transactions: normalizedTransactions,
  }
}

export { useEarningHistory }
